/**
 * An id for **this tab**, so the service can tell a reload from a second tab.
 *
 * It cannot tell them apart by itself: one person with two tabs is one token and one subject, which is
 * why reloading the page used to be met with "another tester is holding this phone" — and why, once
 * that was fixed, a second tab would happily have started a second video reader on a phone that has
 * exactly one hardware encoder.
 *
 * `sessionStorage`, deliberately, and this is the whole trick: it **survives a reload** in the same tab
 * and is **not shared** between tabs. `localStorage` would make every tab look like the same one (back
 * to two readers on one encoder) and a plain module variable would make every reload look like a new
 * tab (back to "it is open elsewhere" about a phone nobody else has open).
 *
 * Not a secret and not an identity — the claim is still keyed on the tester's stable id. This only
 * answers "which window am I".
 */

const KEY = 'tthub.tab'

export function tabId(): string {
  try {
    const found = sessionStorage.getItem(KEY)
    if (found) return found
    // `crypto.randomUUID` is unavailable on plain http (a dev box), and this is not a secret.
    const made = globalThis.crypto?.randomUUID?.() ?? `t-${Math.random().toString(36).slice(2, 10)}`
    sessionStorage.setItem(KEY, made)
    return made
  } catch {
    // Private mode with storage denied: no id means the tab check does not apply, which the service
    // treats as "hand the phone back" — the old behaviour, not a refusal.
    return ''
  }
}
